/**
 * HCEN Hospital — single revenue & booking aggregation engine.
 * Same contract as Dashboard (AppContext.computeStats) and Lambda:
 *   BOOKINGS = OP only, scheduled (confirmed → completed pipeline)
 *   REVENUE  = OP only, checked_in or completed × consultationFee
 *   DATE     = local YYYY-MM-DD (not UTC)
 */

export const localDateKey = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const todayStr = () => localDateKey(new Date());

export const offsetDateStr = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return localDateKey(d);
};

export const weekStartStr = () => offsetDateStr(-6); // today + 6 prior days = 7 days
export const monthStartStr = () => offsetDateStr(-29); // 30 days inclusive

export const isOpBooking = (b) => {
  const t = String(b?.type || b?._raw?.type || '').toLowerCase();
  return t !== 'sos' && t !== 'emergency';
};

/** Raw status from booking record (before auto no-show rules). */
export const rawBookingStatus = (b) =>
  String(b?._raw?.status || b?.status || '').toLowerCase();

/**
 * Auto no-show: appointment date is before today and patient never checked in.
 * Rule — day after the appointment, still confirmed/accepted/pending → no-show.
 */
export const shouldAutoNoShow = (b) => {
  if (!isOpBooking(b)) return false;
  const st = rawBookingStatus(b);
  if (['checked_in', 'completed', 'cancelled', 'canceled', 'rejected', 'no_show', 'no-show', 'rescheduled'].includes(st)) {
    return false;
  }
  if (st === 'rescheduled' || b._raw?.isRescheduled) return false;
  const apptDate = (b.date || b._raw?.date || b._raw?.appointmentDate || '').split('T')[0];
  if (!apptDate) return false;
  return apptDate < todayStr();
};

/** Effective status used everywhere in stats, analytics, and UI. */
export const effectiveBookingStatus = (b) => {
  const st = rawBookingStatus(b);
  if (st === 'no_show' || st === 'no-show') return 'no_show';
  if (shouldAutoNoShow(b)) return 'no_show';
  return st;
};

/** Apply auto no-show to normalized appointments for display + stats. */
export function enrichAppointmentsWithEffectiveStatus(appointments = []) {
  return appointments.map(a => {
    const eff = effectiveBookingStatus(a);
    if (eff !== 'no_show') return a;
    const isAuto = shouldAutoNoShow(a) && !['no_show', 'no-show'].includes(rawBookingStatus(a));
    if (a.status === 'no_show' && !isAuto) return a;
    return { ...a, status: 'no_show', _autoNoShow: isAuto };
  });
}

export const bookingStatus = (b) => effectiveBookingStatus(b);

export const isBookedOpVisit = (b) => {
  const st = effectiveBookingStatus(b);
  return isOpBooking(b) && !['cancelled', 'canceled', 'rejected'].includes(st);
};

export const isScheduledOpBooking = (b) => {
  const st = effectiveBookingStatus(b);
  return isOpBooking(b) &&
    ['confirmed', 'checked_in', 'accepted', 'completed'].includes(st);
};

export const isEarnedBooking = (b) =>
  isOpBooking(b) &&
  (effectiveBookingStatus(b) === 'checked_in' || effectiveBookingStatus(b) === 'completed');

export const bookingFee = (b) => Number(b?.fee ?? b?._raw?.consultationFee ?? b?._raw?.fee ?? 0) || 0;

export const isInRange = (dateStr, from, to) => {
  if (!dateStr) return false;
  return dateStr >= from && dateStr <= to;
};

/** Build inclusive date range for preset or custom period. */
export const getPeriodRange = (period, customFrom, customTo) => {
  const today = todayStr();
  switch (period) {
    case 'today':
      return { from: today, to: today, label: 'Today' };
    case 'week':
      return { from: weekStartStr(), to: today, label: 'This Week (7 days)' };
    case 'month':
      return { from: monthStartStr(), to: today, label: 'This Month (30 days)' };
    case 'custom':
      return {
        from: customFrom || today,
        to: customTo || customFrom || today,
        label: customFrom === customTo
          ? formatDisplayDate(customFrom)
          : `${formatDisplayDate(customFrom)} – ${formatDisplayDate(customTo)}`,
      };
    default:
      return { from: weekStartStr(), to: today, label: 'This Week (7 days)' };
  }
};

export const formatDisplayDate = (dateStr) => {
  if (!dateStr) return '—';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
};

export const fmtRupee = (n) => {
  const num = Number(n) || 0;
  if (num >= 100000) return `₹${(num / 100000).toFixed(2)}L`;
  if (num >= 1000) return `₹${(num / 1000).toFixed(1)}K`;
  if (Math.abs(num - Math.round(num)) > 0.001) return `₹${num.toFixed(2)}`;
  return `₹${Math.round(num).toLocaleString('en-IN')}`;
};

export const fmtRupeeExact = (n) => {
  const num = Number(n) || 0;
  return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

export const pctChange = (current, previous) => {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 1000) / 10;
};

/** List every calendar day in [from, to] inclusive. */
export const eachDayInRange = (from, to) => {
  const days = [];
  const [fy, fm, fd] = from.split('-').map(Number);
  const [ty, tm, td] = to.split('-').map(Number);
  const cur = new Date(fy, fm - 1, fd);
  const end = new Date(ty, tm - 1, td);
  while (cur <= end) {
    days.push(localDateKey(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return days;
};

/**
 * Core stats for a selected date range — used by Reports & Analytics.
 * Doctor revenue uses actual booking fees (what was collected per visit).
 */
export function computeRangeStats(appointments = [], range, doctors = []) {
  const { from, to } = range;
  const op = (appointments || []).filter(isOpBooking);
  const inPeriod = (b) => isInRange(b.date, from, to);

  const periodBookings = op.filter(b => inPeriod(b) && isBookedOpVisit(b));
  const periodEarned = op.filter(b => inPeriod(b) && isEarnedBooking(b));
  const periodNoShows = op.filter(b => inPeriod(b) && effectiveBookingStatus(b) === 'no_show');

  const revenue = periodEarned.reduce((s, b) => s + bookingFee(b), 0);
  const bookingCount = periodBookings.length;
  const earnedCount = periodEarned.length;

  // Previous period of same length for comparison
  const dayCount = eachDayInRange(from, to).length;
  const prevTo = offsetDateStr(-dayCount);
  const prevFrom = offsetDateStr(-(dayCount * 2 - 1));
  const prevEarned = op.filter(b => isInRange(b.date, prevFrom, prevTo) && isEarnedBooking(b));
  const prevBookings = op.filter(b => isInRange(b.date, prevFrom, prevTo) && isBookedOpVisit(b));
  const prevRevenue = prevEarned.reduce((s, b) => s + bookingFee(b), 0);

  // Daily trend — one bar per calendar day (not weekday buckets)
  const dailyTrend = eachDayInRange(from, to).map(date => {
    const dayBookings = op.filter(b => b.date === date && isBookedOpVisit(b));
    const dayEarned = op.filter(b => b.date === date && isEarnedBooking(b));
    const dayNoShows = op.filter(b => b.date === date && effectiveBookingStatus(b) === 'no_show');
    return {
      date,
      label: date.slice(5),
      displayDate: formatDisplayDate(date),
      bookings: dayBookings.length,
      revenue: dayEarned.reduce((s, b) => s + bookingFee(b), 0),
      earnedVisits: dayEarned.length,
      noShows: dayNoShows.length,
    };
  });

  // Doctor revenue — separate, accurate per-doctor payout basis
  const docLookup = {};
  doctors.forEach(d => {
    if (d?.name) docLookup[d.name] = d;
  });

  const doctorMap = {};

  const doctorName = (b) => (b.doctor && b.doctor !== '·' ? b.doctor : 'Unassigned');

  const ensureDoc = (b) => {
    const name = doctorName(b);
    if (!doctorMap[name]) {
      const doc = docLookup[name];
      doctorMap[name] = {
        name,
        specialization: doc?.specialization || b.specialization || '—',
        currentFee: doc?.fee != null ? Number(doc.fee) : null,
        patients: 0,
        scheduled: 0,
        revenue: 0,
      };
    }
    return name;
  };

  periodBookings.forEach(b => {
    ensureDoc(b);
    doctorMap[doctorName(b)].scheduled += 1;
  });

  periodEarned.forEach(b => {
    ensureDoc(b);
    const name = doctorName(b);
    doctorMap[name].patients += 1;
    doctorMap[name].revenue += bookingFee(b);
  });

  const doctorRevenue = Object.values(doctorMap)
    .map(d => ({
      ...d,
      avgFee: d.patients > 0 ? d.revenue / d.patients : 0,
    }))
    .sort((a, b) => b.revenue - a.revenue || b.patients - a.patients);

  // Patient-level rows for the period (for hospital review)
  const patientRows = [...periodBookings]
    .sort((a, b) => (b.date || '').localeCompare(a.date || '') || (a.time || '').localeCompare(b.time || ''))
    .map(b => ({
      id: b.id,
      patientName: b.patientName,
      doctor: b.doctor,
      specialization: b.specialization,
      date: b.date,
      time: b.time,
      status: b.status,
      rawStatus: bookingStatus(b),
      fee: bookingFee(b),
      earned: isEarnedBooking(b),
      revenue: isEarnedBooking(b) ? bookingFee(b) : 0,
    }));

  // Status breakdown within period (uses effective status incl. auto no-show)
  const statusBreakdown = {
    checked_in: op.filter(b => inPeriod(b) && effectiveBookingStatus(b) === 'checked_in').length,
    completed: op.filter(b => inPeriod(b) && effectiveBookingStatus(b) === 'completed').length,
    confirmed: op.filter(b => inPeriod(b) && ['confirmed', 'accepted'].includes(effectiveBookingStatus(b)) && !b._raw?.isRescheduled).length,
    pending: op.filter(b => inPeriod(b) && effectiveBookingStatus(b) === 'pending').length,
    cancelled: op.filter(b => inPeriod(b) && ['cancelled', 'canceled', 'rejected'].includes(effectiveBookingStatus(b))).length,
    rescheduled: op.filter(b => inPeriod(b) && (b._raw?.isRescheduled || effectiveBookingStatus(b) === 'rescheduled')).length,
    no_show: periodNoShows.length,
  };

  const noShowRows = periodNoShows
    .map(b => ({
      id: b.id,
      patientName: b.patientName,
      doctor: b.doctor,
      date: b.date,
      time: b.time,
      fee: bookingFee(b),
      auto: !!b._autoNoShow || shouldAutoNoShow(b),
    }))
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  // Peak hours within period
  const peakHours = {};
  periodBookings.forEach(b => {
    const t = b.time || b._raw?.slot || b._raw?.appointmentTime || '';
    const h = parseInt(String(t).split(':')[0], 10);
    if (!isNaN(h) && h >= 0 && h <= 23) {
      peakHours[h] = (peakHours[h] || 0) + 1;
    }
  });

  // Monthly trend (last 6 months) — all-time from appointments
  const monthlyTrend = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - i);
    const monthStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString('default', { month: 'short', year: '2-digit' });
    const rev = op
      .filter(b => isEarnedBooking(b) && b.date?.startsWith(monthStr))
      .reduce((s, b) => s + bookingFee(b), 0);
    monthlyTrend.push({ month: label, revenue: rev });
  }

  return {
    revenue,
    bookingCount,
    earnedCount,
    revenueGrowth: pctChange(revenue, prevRevenue),
    bookingGrowth: pctChange(bookingCount, prevBookings.length),
    dailyTrend,
    doctorRevenue,
    patientRows,
    statusBreakdown,
    peakHours,
    monthlyTrend,
    noShowRows,
    noShowCount: periodNoShows.length,
    prevRevenue,
    prevBookingCount: prevBookings.length,
  };
}

/** Dashboard-aligned snapshot cards (today / yesterday / week / month / total). */
export function computeDashboardSnapshot(appointments = []) {
  const op = (appointments || []).filter(isOpBooking);
  const earned = op.filter(isEarnedBooking);
  const today = todayStr();
  const yesterday = offsetDateStr(-1);
  const weekFrom = weekStartStr();
  const monthFrom = monthStartStr();

  const sumEarned = (from, to) =>
    earned.filter(b => isInRange(b.date, from, to)).reduce((s, b) => s + bookingFee(b), 0);

  const countScheduled = (from, to) =>
    op.filter(b => isInRange(b.date, from, to) && isBookedOpVisit(b)).length;

  const todayRev = sumEarned(today, today);
  const yestRev = sumEarned(yesterday, yesterday);

  return {
    today: { revenue: todayRev, bookings: countScheduled(today, today) },
    yesterday: { revenue: yestRev, bookings: countScheduled(yesterday, yesterday) },
    week: { revenue: sumEarned(weekFrom, today), bookings: countScheduled(weekFrom, today) },
    month: { revenue: sumEarned(monthFrom, today), bookings: countScheduled(monthFrom, today) },
    total: { revenue: earned.reduce((s, b) => s + bookingFee(b), 0), bookings: op.filter(isBookedOpVisit).length },
    todayGrowth: pctChange(todayRev, yestRev),
  };
}

/** Export patient + doctor rows as CSV string. */
export function exportRangeCsv(stats, range, hospitalName = 'Hospital') {
  const lines = [`HCEN Revenue Report — ${hospitalName}`, `Period: ${range.label}`, `Generated: ${new Date().toLocaleString('en-IN')}`, ''];
  lines.push('DOCTOR REVENUE (earned visits only — checked in / completed)');
  lines.push('Doctor,Specialization,Patients Seen,Scheduled Visits,Revenue (₹),Avg Fee (₹)');
  stats.doctorRevenue.forEach(d => {
    lines.push(`"${d.name}","${d.specialization}",${d.patients},${d.scheduled},${d.revenue.toFixed(2)},${d.avgFee.toFixed(2)}`);
  });
  lines.push('');
  lines.push('PATIENT VISITS');
  lines.push('Date,Time,Patient,Doctor,Status,Fee (₹),Earned Revenue (₹)');
  stats.patientRows.forEach(p => {
    lines.push(`${p.date},${p.time || ''},"${p.patientName}","${p.doctor}",${p.rawStatus},${p.fee.toFixed(2)},${p.revenue.toFixed(2)}`);
  });
  lines.push('');
  lines.push(`Total Revenue,${stats.revenue.toFixed(2)}`);
  lines.push(`Total Bookings,${stats.bookingCount}`);
  lines.push(`Earned Visits,${stats.earnedCount}`);
  return lines.join('\n');
}
